import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const api = axios.create({
        baseURL: API,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    // Update headers when token changes
    useEffect(() => {
        if (token) {
            api.defaults.headers.Authorization = `Bearer ${token}`;
        } else {
            delete api.defaults.headers.Authorization;
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchUser();
        } else {
            setLoading(false);
        }
    }, []);

    const fetchUser = async () => {
        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password, rememberMe = false) => {
        const response = await api.post('/auth/login', { email, password, remember_me: rememberMe });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        api.defaults.headers.Authorization = `Bearer ${access_token}`;
        setUser(userData);
        return userData;
    };

    const register = async (email, password, name, receiveEmails = true, birthday = null, gender = null, language = 'en') => {
        const response = await api.post('/auth/register', {
            email,
            password,
            name,
            birthday: birthday || null,
            gender: gender || null,
            language: language || 'en',
            receive_emails: receiveEmails
        });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        api.defaults.headers.Authorization = `Bearer ${access_token}`;
        setUser(userData);
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        delete api.defaults.headers.Authorization;
    };

    const updatePreferences = async (preferences) => {
        await api.put('/auth/preferences', preferences);
        setUser({ ...user, theme_preferences: preferences });
    };

    const updateSubscription = (newSubscription) => {
        setUser({ ...user, subscription: newSubscription });
    };

    const refreshUser = async () => {
        if (token) {
            await fetchUser();
        }
    };

    const checkFeature = (feature) => {
        const subscription = user?.subscription || { tier: 'basic', addons: [] };
        const tier = subscription.tier;
        const addons = subscription.addons || [];

        // Feature mappings
        const basicFeatures = ['basic_notifications', 'journal_lock', 'unlimited_journaling'];
        const plusFeatures = [...basicFeatures, 'ai_trends', 'mood_streaks', 'custom_tags', 
            'search_filter', 'journal_insights', 'journal_export', 'partners_chat', 'scheduled_checkins'];
        const completeFeatures = [...plusFeatures, 'deeper_insights', 'unlimited_partners'];

        if (tier === 'complete') return true;
        if (tier === 'plus' && plusFeatures.includes(feature)) return true;
        if (basicFeatures.includes(feature)) return true;

        // Check addons
        if (addons.includes('deeper_insights') && feature === 'deeper_insights') return true;
        if (addons.includes('partners_pro') && ['unlimited_partners', 'partners_chat'].includes(feature)) return true;

        return false;
    };

    const getPartnerLimit = () => {
        const subscription = user?.subscription || { tier: 'basic', addons: [] };
        if (subscription.tier === 'complete' || subscription.addons?.includes('partners_pro')) return -1;
        if (subscription.tier === 'plus') return 3;
        return 1;
    };

    const getHistoryDays = () => {
        const subscription = user?.subscription || { tier: 'basic' };
        if (subscription.tier === 'plus' || subscription.tier === 'complete') return -1;
        return 7;
    };

    return (
        <AuthContext.Provider value={{
            user,
            token,
            loading,
            login,
            register,
            logout,
            updatePreferences,
            updateSubscription,
            refreshUser,
            checkFeature,
            getPartnerLimit,
            getHistoryDays,
            api,
            isAuthenticated: !!user
        }}>
            {children}
        </AuthContext.Provider>
    );
};
